import { QuestionMarkCircleIcon } from '@heroicons/react/20/solid';
import {
  AddressComponentConfig,
  AddressComponentVisibility,
  AddressPlaceType,
  HtmlStepperFieldFlat,
  HtmlStepperFieldOption,
  HtmlStepperFieldType,
} from '@superfunnel/types';
import ChipsAutocomplete from '@superfunnel/ui-core/ChipsAutocomplete';
import Button from '@superfunnel/ui-core/v2/Button';
import Checkbox from '@superfunnel/ui-core/v2/Checkbox';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import Modal from '@superfunnel/ui-core/v2/Modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import Switch from '@superfunnel/ui-core/v2/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@superfunnel/ui-core/v2/Tooltip';
import { Trash } from 'iconoir-react';
import { useCallback, useEffect, useState } from 'react';
import countries from '../../assets/countries.json';
import SortableList from '../SortableList';

// ============================================================================
// Types
// ============================================================================

/**
 * Modal data format for editing fields.
 * Uses HtmlStepperFieldFlat for form state management.
 */
export type EditFieldModalData = HtmlStepperFieldFlat;

interface Props {
  open: boolean;
  data: EditFieldModalData | null;
  existingFieldIds: string[];
  onClose: () => void;
  onSave: (data: EditFieldModalData) => void;
}

// ============================================================================
// Constants
// ============================================================================

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  email: 'Email',
  tel: 'Phone',
  number: 'Number',
  textarea: 'Text Area',
  date: 'Date',
  country: 'Country',
  state: 'State',
  phone: 'Phone (with country)',
  address: 'Address',
  select: 'Select',
  multiChoice: 'Multi-Choice',
  checkbox: 'Checkbox',
  range: 'Range Slider',
  hidden: 'Hidden',
};

/** Field types that can be switched between */
const TEXT_COMPATIBLE_TYPES: HtmlStepperFieldType[] = [
  'text',
  'email',
  'tel',
  'textarea',
];

/** Data-driven field types (options managed by system) */
const DATA_DRIVEN_TYPES: HtmlStepperFieldType[] = ['country', 'state', 'phone'];

/** Field types that support options */
const OPTION_TYPES: HtmlStepperFieldType[] = ['select', 'multiChoice'];

/** Address place types for configuration */
const ADDRESS_PLACE_TYPES: { value: AddressPlaceType; label: string }[] = [
  { value: 'address', label: 'Address' },
  { value: 'geocode', label: 'Geocode' },
  { value: 'establishment', label: 'Establishment' },
  { value: '(regions)', label: 'Regions' },
  { value: '(cities)', label: 'Cities' },
];

/** Address component visibility options */
const ADDRESS_VISIBILITY_OPTIONS: {
  value: AddressComponentVisibility;
  label: string;
}[] = [
  { value: 'hidden', label: 'Hidden' },
  { value: 'optional', label: 'Optional' },
  { value: 'required', label: 'Required' },
];

/** Address components for configuration */
const ADDRESS_COMPONENTS: {
  key: keyof AddressComponentConfig;
  label: string;
}[] = [
  { key: 'streetNumber', label: 'Street Number' },
  { key: 'route', label: 'Street' },
  { key: 'secondaryLine', label: 'Apt/Suite/Unit' },
  { key: 'locality', label: 'City' },
  { key: 'administrativeAreaLevel1', label: 'State/Province' },
  { key: 'postalCode', label: 'Postal Code' },
  { key: 'country', label: 'Country' },
];

// ============================================================================
// Helper Functions
// ============================================================================

const getCompatibleTypes = (
  currentType: string,
): HtmlStepperFieldType[] | null => {
  if (TEXT_COMPATIBLE_TYPES.includes(currentType as HtmlStepperFieldType)) {
    return TEXT_COMPATIBLE_TYPES;
  }
  // multiChoice can switch between single/multiple via the multiple prop
  return null;
};

// ============================================================================
// Component
// ============================================================================

export default function EditHTMLStepperFieldModal({
  open,
  data: initialData,
  existingFieldIds,
  onClose,
  onSave,
}: Props) {
  const [data, setData] = useState<EditFieldModalData | null>(null);
  const [fieldIdError, setFieldIdError] = useState<string | null>(null);
  const [optionErrors, setOptionErrors] = useState<Record<number, string>>({});

  // Reset form when modal opens
  useEffect(() => {
    if (!open) return;
    setData(initialData ? { ...initialData } : null);
    setFieldIdError(null);
    setOptionErrors({});
  }, [open, initialData]);

  // Validate field ID uniqueness
  const validateFieldId = useCallback(
    (fieldId: string): string | null => {
      if (!fieldId.trim()) {
        return 'Field ID is required';
      }
      if (!/^[a-zA-Z_][a-zA-Z0-9_-]*$/.test(fieldId)) {
        return 'Field ID must start with a letter or underscore and contain only letters, numbers, underscores, and hyphens';
      }
      // Check uniqueness (excluding current field's original ID)
      const otherFieldIds = existingFieldIds.filter(
        (id) => id !== initialData?.id,
      );
      if (otherFieldIds.includes(fieldId)) {
        return 'This field ID is already used in another field';
      }
      return null;
    },
    [existingFieldIds, initialData?.id],
  );

  // Validate option values
  const validateOptions = useCallback(
    (options: HtmlStepperFieldOption[]): Record<number, string> => {
      const errors: Record<number, string> = {};
      const seenValues = new Set<string>();

      options.forEach((option, index) => {
        if (!option.value.trim()) {
          errors[index] = 'Value is required';
        } else if (seenValues.has(option.value)) {
          errors[index] = 'Duplicate value';
        } else {
          seenValues.add(option.value);
        }
      });

      return errors;
    },
    [],
  );

  const handleSave = useCallback(() => {
    if (!data) return;

    // Validate field ID
    const idError = validateFieldId(data.id);
    if (idError) {
      setFieldIdError(idError);
      return;
    }

    // Validate options if applicable
    if (OPTION_TYPES.includes(data.type)) {
      const optErrors = validateOptions(data.options || []);
      if (Object.keys(optErrors).length > 0) {
        setOptionErrors(optErrors);
        return;
      }
    }

    onSave(data);
  }, [data, validateFieldId, validateOptions, onSave]);

  // Options reorder handler for SortableList
  const handleOptionsReorder = useCallback(
    (reorderedItems: (HtmlStepperFieldOption & { id: string })[]) => {
      // Convert back to options without the id field
      const newOptions = reorderedItems.map(({ id, ...rest }) => rest);
      setData((prev) => (prev ? { ...prev, options: newOptions } : null));
    },
    [],
  );

  const handleDeleteOption = useCallback((index: number) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            options: prev.options?.filter((_, i) => i !== index),
          }
        : null,
    );
    setOptionErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  }, []);

  const handleUpdateOptionValue = useCallback(
    (index: number, value: string) => {
      setData((prev) =>
        prev
          ? {
              ...prev,
              options: prev.options?.map((o, i) =>
                i === index ? { ...o, value } : o,
              ),
            }
          : null,
      );
      // Clear error for this option when user types
      setOptionErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    },
    [],
  );

  if (!data) return null;

  const fieldType = data.type;
  const compatibleTypes = getCompatibleTypes(data.type);
  const isDataDriven = DATA_DRIVEN_TYPES.includes(fieldType);
  const supportsOptions = OPTION_TYPES.includes(fieldType);
  const isHidden = fieldType === 'hidden';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Field"
      actionsAlignment="end"
      width="2xl"
      actions={[
        {
          type: 'secondary',
          title: 'Cancel',
          onClick: onClose,
        },
        {
          type: 'primary',
          title: 'Save',
          onClick: handleSave,
        },
      ]}
    >
      <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto px-1">
        {/* Basic Section */}
        <div className="flex flex-col gap-3">
          {/* Field ID */}
          <Input
            title="Field ID"
            value={data.id}
            onChange={(e) => {
              setData((prev) =>
                prev ? { ...prev, id: e.target.value } : null,
              );
              setFieldIdError(null);
            }}
            placeholder="e.g., firstName, email"
            validation={
              fieldIdError
                ? { status: 'error', message: fieldIdError }
                : undefined
            }
          />

          {/* Field Type */}
          {compatibleTypes ? (
            <div className="flex flex-col gap-1">
              <FieldTitle title="Field Type" />
              <Select
                value={data.type}
                onValueChange={(newType) =>
                  setData((prev) =>
                    prev
                      ? { ...prev, type: newType as HtmlStepperFieldType }
                      : null,
                  )
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {compatibleTypes.map((t) => (
                    <SelectItem key={t} value={t}>
                      {FIELD_TYPE_LABELS[t] || t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <FieldTitle title="Field Type" />
              <Select value={data.type} disabled>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key={data.type} value={data.type}>
                    {FIELD_TYPE_LABELS[data.type] || data.type}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <Separator />
        </div>

        {/* Behavior Section */}
        {!isHidden && (
          <>
            <div className="flex flex-col gap-3">
              <div className="text-base-700 text-sm font-medium">Behavior</div>

              {/* Required */}
              <div className="flex items-center justify-between">
                <FieldTitle title="Required" />
                <Switch
                  checked={data.required ?? false}
                  onCheckedChange={(required) =>
                    setData((prev) => (prev ? { ...prev, required } : null))
                  }
                />
              </div>

              {/* Protected */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <FieldTitle title="Protected" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <QuestionMarkCircleIcon className="size-4 cursor-help text-gray-400 transition-colors hover:text-gray-600" />
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p>
                        Data entered here will be forwarded to your webhook and
                        not stored in our system.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Switch
                  checked={data.protected ?? false}
                  onCheckedChange={(protected_) =>
                    setData((prev) =>
                      prev ? { ...prev, protected: protected_ } : null,
                    )
                  }
                />
              </div>

              {/* MultiChoice: Multiple selection toggle */}
              {fieldType === 'multiChoice' && (
                <>
                  <div className="flex items-center justify-between">
                    <FieldTitle title="Allow Multiple Selections" />
                    <Switch
                      checked={data.multiple ?? false}
                      onCheckedChange={(multiple) =>
                        setData((prev) =>
                          prev
                            ? {
                                ...prev,
                                multiple,
                                // Clear autoAdvance if multiple is enabled
                                autoAdvance: multiple
                                  ? false
                                  : prev.autoAdvance,
                              }
                            : null,
                        )
                      }
                    />
                  </div>

                  {/* MultiChoice with multiple: Min/Max selections */}
                  {fieldType === 'multiChoice' && data.multiple && (
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        title="Min Selections"
                        type="number"
                        value={data.min ?? ''}
                        onChange={(e) =>
                          setData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  min: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                }
                              : null,
                          )
                        }
                        placeholder="1"
                      />
                      <Input
                        title="Max Selections"
                        type="number"
                        value={data.max ?? ''}
                        onChange={(e) =>
                          setData((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  max: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                }
                              : null,
                          )
                        }
                        placeholder="No limit"
                      />
                    </div>
                  )}
                </>
              )}

              {/* MultiChoice single: Auto-advance */}
              {fieldType === 'multiChoice' && !data.multiple && (
                <div className="flex items-center justify-between">
                  <FieldTitle title="Auto-advance on Selection" />
                  <Switch
                    checked={data.autoAdvance ?? false}
                    onCheckedChange={(autoAdvance) =>
                      setData((prev) =>
                        prev ? { ...prev, autoAdvance } : null,
                      )
                    }
                  />
                </div>
              )}

              {/* Default Value */}
              <Input
                title="Default Value"
                value={data.defaultValue ?? ''}
                onChange={(e) =>
                  setData((prev) =>
                    prev
                      ? { ...prev, defaultValue: e.target.value || '' }
                      : null,
                  )
                }
                placeholder="Pre-filled value (optional)"
              />
            </div>
            <Separator />
          </>
        )}

        {/* Hidden field: Only default value */}
        {isHidden && (
          <>
            <div className="flex flex-col gap-3">
              <div className="text-base-700 text-sm font-medium">Value</div>
              <Input
                title="Default Value"
                value={data.defaultValue ?? ''}
                onChange={(e) =>
                  setData((prev) =>
                    prev
                      ? { ...prev, defaultValue: e.target.value || '' }
                      : null,
                  )
                }
                placeholder="Hidden field value"
              />
            </div>
            <Separator />
          </>
        )}

        {/* Validation Section (type-specific) */}
        {!isHidden && !isDataDriven && (
          <>
            {/* Text-based fields: Pattern */}
            {TEXT_COMPATIBLE_TYPES.includes(fieldType) && (
              <>
                <div className="flex flex-col gap-3">
                  <div className="text-base-700 text-sm font-medium">
                    Validation
                  </div>
                  <Input
                    title="Pattern (Regex)"
                    value={data.pattern ?? ''}
                    onChange={(e) =>
                      setData((prev) =>
                        prev
                          ? { ...prev, pattern: e.target.value || undefined }
                          : null,
                      )
                    }
                    placeholder="e.g., ^[A-Za-z]+$"
                  />
                </div>
                <Separator />
              </>
            )}

            {/* Number field: Min/Max Value */}
            {fieldType === 'number' && (
              <>
                <div className="flex flex-col gap-3">
                  <div className="text-base-700 text-sm font-medium">
                    Validation
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      title="Min Value"
                      type="number"
                      value={data.minValue ?? ''}
                      onChange={(e) =>
                        setData((prev) =>
                          prev
                            ? {
                                ...prev,
                                minValue: e.target.value
                                  ? Number(e.target.value)
                                  : undefined,
                              }
                            : null,
                        )
                      }
                      placeholder="No minimum"
                    />
                    <Input
                      title="Max Value"
                      type="number"
                      value={data.maxValue ?? ''}
                      onChange={(e) =>
                        setData((prev) =>
                          prev
                            ? {
                                ...prev,
                                maxValue: e.target.value
                                  ? Number(e.target.value)
                                  : undefined,
                              }
                            : null,
                        )
                      }
                      placeholder="No maximum"
                    />
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* Range field: Min/Max Value + Step */}
            {fieldType === 'range' && (
              <>
                <div className="flex flex-col gap-3">
                  <div className="text-base-700 text-sm font-medium">
                    Validation
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      title="Min Value"
                      type="number"
                      value={data.minValue ?? ''}
                      onChange={(e) =>
                        setData((prev) =>
                          prev
                            ? {
                                ...prev,
                                minValue: e.target.value
                                  ? Number(e.target.value)
                                  : undefined,
                              }
                            : null,
                        )
                      }
                      placeholder="0"
                    />
                    <Input
                      title="Max Value"
                      type="number"
                      value={data.maxValue ?? ''}
                      onChange={(e) =>
                        setData((prev) =>
                          prev
                            ? {
                                ...prev,
                                maxValue: e.target.value
                                  ? Number(e.target.value)
                                  : undefined,
                              }
                            : null,
                        )
                      }
                      placeholder="100"
                    />
                    <Input
                      title="Step"
                      type="number"
                      value={data.step ?? ''}
                      onChange={(e) =>
                        setData((prev) =>
                          prev
                            ? {
                                ...prev,
                                step: e.target.value
                                  ? Number(e.target.value)
                                  : undefined,
                              }
                            : null,
                        )
                      }
                      placeholder="1"
                    />
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* Date field: Min/Max Date */}
            {fieldType === 'date' && (
              <>
                <div className="flex flex-col gap-3">
                  <div className="text-base-700 text-sm font-medium">
                    Validation
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      title="Min Date"
                      type="date"
                      value={data.minDate ?? ''}
                      onChange={(e) =>
                        setData((prev) =>
                          prev
                            ? {
                                ...prev,
                                minDate: e.target.value || undefined,
                              }
                            : null,
                        )
                      }
                    />
                    <Input
                      title="Max Date"
                      type="date"
                      value={data.maxDate ?? ''}
                      onChange={(e) =>
                        setData((prev) =>
                          prev
                            ? {
                                ...prev,
                                maxDate: e.target.value || undefined,
                              }
                            : null,
                        )
                      }
                    />
                  </div>
                </div>
                <Separator />
              </>
            )}

            {/* Address field configuration */}
            {fieldType === 'address' && (
              <>
                <div className="flex flex-col gap-4">
                  <div className="text-base-700 text-sm font-medium">
                    Address Configuration
                  </div>

                  {/* Restrict to Countries */}
                  <ChipsAutocomplete<(typeof countries)[0]>
                    title="Restrict to Countries"
                    getValue={(c) => `${c.name} (${c.code})`}
                    renderItem={(c) => (
                      <span className="flex items-center gap-2">
                        <span className="text-sm">{c.name}</span>
                        <span className="text-xs text-gray-500">{c.code}</span>
                      </span>
                    )}
                    selected={
                      (data.restrictCountries ?? [])
                        .map((code: string) =>
                          countries.find((c) => c.code === code),
                        )
                        .filter(Boolean) as typeof countries
                    }
                    onChange={(s) => {
                      setData((prev) =>
                        prev
                          ? {
                              ...prev,
                              restrictCountries: s.map((c) => c.code),
                            }
                          : null,
                      );
                    }}
                    getOptionId={(c) => c.code}
                    options={countries}
                  />

                  {/* Place Types */}
                  <div className="flex flex-col gap-1">
                    <FieldTitle title="Place Types" />
                    <div className="flex flex-wrap gap-2">
                      {ADDRESS_PLACE_TYPES.map((placeType) => {
                        const currentTypes = data.placeTypes ?? ['address'];
                        const isSelected = currentTypes.includes(
                          placeType.value,
                        );
                        return (
                          <button
                            key={placeType.value}
                            type="button"
                            onClick={() => {
                              const newTypes = isSelected
                                ? currentTypes.filter(
                                    (t) => t !== placeType.value,
                                  )
                                : [...currentTypes, placeType.value];
                              setData((prev) =>
                                prev
                                  ? {
                                      ...prev,
                                      placeTypes:
                                        newTypes.length > 0
                                          ? newTypes
                                          : ['address'],
                                    }
                                  : null,
                              );
                            }}
                            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                              isSelected
                                ? 'bg-brand-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {placeType.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Allow editing after selection */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="editable-after-selection"
                      checked={data.editableAfterSelection || false}
                      onCheckedChange={(checked) => {
                        setData((prev) =>
                          prev
                            ? {
                                ...prev,
                                editableAfterSelection: checked as boolean,
                              }
                            : null,
                        );
                      }}
                    />
                    <label
                      className="text-sm font-medium text-gray-900"
                      htmlFor="editable-after-selection"
                    >
                      Allow editing address components after selection
                    </label>
                  </div>

                  {/* Component visibility settings */}
                  {data.editableAfterSelection && (
                    <div className="flex flex-col gap-2 rounded-md border border-gray-200 p-3">
                      <FieldTitle title="Component Visibility" />
                      <div className="grid grid-cols-2 gap-2">
                        {ADDRESS_COMPONENTS.map((comp) => (
                          <div
                            key={String(comp.key)}
                            className="flex items-center gap-2"
                          >
                            <span className="min-w-[100px] text-sm text-gray-600">
                              {comp.label}
                            </span>
                            <Select
                              value={
                                data.componentConfig?.[comp.key] ?? 'optional'
                              }
                              onValueChange={(
                                value: AddressComponentVisibility,
                              ) => {
                                setData((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        componentConfig: {
                                          ...prev.componentConfig,
                                          [comp.key]: value,
                                        },
                                      }
                                    : null,
                                );
                              }}
                            >
                              <SelectTrigger className="h-8 w-[100px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ADDRESS_VISIBILITY_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Allow manual entry */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="allow-manual-entry"
                      checked={data.allowManualEntry || false}
                      onCheckedChange={(checked) => {
                        setData((prev) =>
                          prev
                            ? {
                                ...prev,
                                allowManualEntry: checked as boolean,
                              }
                            : null,
                        );
                      }}
                    />
                    <label
                      className="text-sm font-medium text-gray-900"
                      htmlFor="allow-manual-entry"
                    >
                      Allow manual entry when address not found
                    </label>
                  </div>

                  {/* Language */}
                  <div className="flex flex-col gap-1">
                    <FieldTitle title="Language (optional)" />
                    <Select
                      value={data.language || 'auto'}
                      onValueChange={(value: string) => {
                        setData((prev) =>
                          prev
                            ? {
                                ...prev,
                                language: value === 'auto' ? undefined : value,
                              }
                            : null,
                        );
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Default (browser language)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">
                          Default (browser language)
                        </SelectItem>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="es">Spanish</SelectItem>
                        <SelectItem value="fr">French</SelectItem>
                        <SelectItem value="de">German</SelectItem>
                        <SelectItem value="it">Italian</SelectItem>
                        <SelectItem value="pt">Portuguese</SelectItem>
                        <SelectItem value="nl">Dutch</SelectItem>
                        <SelectItem value="pl">Polish</SelectItem>
                        <SelectItem value="ru">Russian</SelectItem>
                        <SelectItem value="ja">Japanese</SelectItem>
                        <SelectItem value="ko">Korean</SelectItem>
                        <SelectItem value="zh">Chinese</SelectItem>
                        <SelectItem value="ar">Arabic</SelectItem>
                        <SelectItem value="he">Hebrew</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Separator />
              </>
            )}
          </>
        )}

        {/* Options Section */}
        {supportsOptions && !isDataDriven && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="text-base-700 text-sm font-medium">Options</div>
              {/* Add/Duplicate disabled - requires HTML element creation via AI agent */}
            </div>

            {data.options && data.options.length > 0 ? (
              <SortableList
                items={data.options.map((option, index) => ({
                  ...option,
                  id: option.label || `option-${index}`,
                }))}
                onChange={handleOptionsReorder}
                renderItemContent={(option, index) => (
                  <div className="flex-1">
                    <Input
                      value={option.value}
                      onChange={(e) =>
                        handleUpdateOptionValue(index, e.target.value)
                      }
                      onClick={(e) => e.stopPropagation()}
                      placeholder="Option value"
                      validation={
                        optionErrors[index]
                          ? {
                              status: 'error',
                              message: optionErrors[index],
                            }
                          : undefined
                      }
                    />
                  </div>
                )}
                renderActions={(_option, index) => (
                  <>
                    {/* Duplicate disabled - requires HTML element creation via AI agent */}
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => handleDeleteOption(index)}
                      title="Delete"
                      className="text-red-500 hover:text-red-600"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </>
                )}
              />
            ) : (
              <div className="text-base-400 rounded-lg border border-dashed p-4 text-center text-sm">
                No options defined. Click &ldquo;Add Option&rdquo; to create
                one.
              </div>
            )}
          </div>
        )}

        {/* Data-driven field info */}
        {isDataDriven && (
          <div className="bg-base-50 text-base-600 rounded-lg p-3 text-sm">
            Options for {FIELD_TYPE_LABELS[fieldType]} fields are provided by
            the system and cannot be edited.
          </div>
        )}
      </div>
    </Modal>
  );
}
